import type { THygieneTaskType } from '@/react-query/insights/types';

import type { THygieneRates } from '../types';

import { calculateOverallHygieneRates } from './hygieneRates';

// Hygiene
export const getHygieneOpportunityByTask = (
  insights?: THygieneRates,
  previous?: boolean,
) => {
  if (!insights) return undefined;
  // Consolidate unique keys from both opportunity.tasks and production.tasks
  const oppKeys = Object.keys(insights.opportunity.tasks);
  const prodKeys = Object.keys(insights.production.tasks);
  const taskKeys = Array.from(
    new Set([...oppKeys, ...prodKeys]),
  ) as THygieneTaskType[];

  const opportunityByTask = taskKeys
    .map((task) => {
      // Only add if the data exists
      const oppData = insights.opportunity.tasks[task];
      const prodData = insights.production.tasks[task];
      if (!oppData && !prodData) return undefined;
      return {
        task,
        opportunity: previous ? oppData?.previous : oppData?.current,
        completed: previous ? prodData?.previous : prodData?.current,
      };
    })
    .filter((task) => task !== undefined)
    .filter((task) => {
      return task.opportunity || task.completed;
    });

  return opportunityByTask;
};

export const getHygieneOpportunity = (insights?: THygieneRates) => {
  if (!insights) return undefined;

  const opportunityByTx = getHygieneOpportunityByTask(insights);
  // Filter out any undefined tx values for type safety
  const validOpportunityByTx = (opportunityByTx || []).filter(Boolean) as {
    opportunity: number;
    completed: number;
  }[];
  const overallOpportunity = validOpportunityByTx.reduce(
    (acc, tx) => {
      return {
        opportunity: acc.opportunity + tx.opportunity,
        completed: acc.completed + tx.completed,
      };
    },
    {
      opportunity: 0,
      completed: 0,
    },
  );

  const previousOpportunityByTx = getHygieneOpportunityByTask(insights, true);
  const validPreviousOpportunityByTx = (previousOpportunityByTx || []).filter(
    Boolean,
  ) as { opportunity: number; completed: number }[];
  const previousOverallOpportunity = validPreviousOpportunityByTx.reduce(
    (acc, tx) => {
      return {
        opportunity: acc.opportunity + tx.opportunity,
        completed: acc.completed + tx.completed,
      };
    },
    {
      opportunity: 0,
      completed: 0,
    },
  );
  return {
    current: overallOpportunity,
    previous: previousOverallOpportunity,
  };
};

export const getOverallHygieneOpportunityByTask = (
  insights?: THygieneRates[],
  enabledTaskTypes?: THygieneTaskType[],
) => {
  if (!insights || !insights.length) return undefined;

  const overallRates = calculateOverallHygieneRates(insights, enabledTaskTypes);
  const overallOpportunityByTask = getHygieneOpportunityByTask(overallRates);

  return overallOpportunityByTask;
};

export const getOverallHygieneOpportunity = (
  insights?: THygieneRates[],
  enabledTaskTypes?: THygieneTaskType[],
) => {
  if (!insights || !insights.length) return undefined;

  const overallOpportunityByTask = calculateOverallHygieneRates(
    insights,
    enabledTaskTypes,
  );
  const overallOpportunity = getHygieneOpportunity(overallOpportunityByTask);
  return overallOpportunity;
};
